import { useEffect } from 'react';

import { Icon } from './Icon';
import { useIsMobile } from '../../lib/useBreakpoint';

/**
 * Toast — branded transient notification card. Used to replace window.alert()
 * for error/success/info feedback. Normally rendered via ToastProvider
 * (lib/toast.jsx); this file is just the visual + auto-dismiss timer.
 *
 * Props:
 *   variant   — 'error' (default for failed actions), 'success', 'info'
 *   title     — optional short headline
 *   message   — body copy (string or node)
 *   duration  — ms before auto-dismiss; pass 0 to disable auto-dismiss
 *   onDismiss — required; called when timer expires or the user clicks X
 */
const VARIANT_STYLES = {
  error: {
    border:  'rgba(248,113,113,0.45)',
    accent:  'var(--danger)',
    bgTint:  'var(--danger-bg)',
    Icon:    Icon.alert,
  },
  success: {
    border:  'rgba(74,222,128,0.45)',
    accent:  'var(--ok)',
    bgTint:  'var(--ok-bg)',
    Icon:    Icon.check,
  },
  info: {
    border:  'rgba(96,165,250,0.45)',
    accent:  'var(--info)',
    bgTint:  'var(--info-bg)',
    Icon:    Icon.alert,
  },
};

export default function Toast({
  variant = 'error',
  title,
  message,
  duration = 4500,
  onDismiss,
}) {
  const v = VARIANT_STYLES[variant] || VARIANT_STYLES.error;

  useEffect(() => {
    if (!duration) return;
    const t = setTimeout(() => onDismiss?.(), duration);
    return () => clearTimeout(t);
  }, [duration, onDismiss]);

  return (
    <div
      role={variant === 'error' ? 'alert' : 'status'}
      aria-live={variant === 'error' ? 'assertive' : 'polite'}
      style={{
        display: 'flex',
        gap: 10,
        padding: '12px 14px',
        background: 'var(--surface)',
        border: `1px solid ${v.border}`,
        borderRadius: 12,
        boxShadow: 'var(--shadow-md)',
        minWidth: 240,
        maxWidth: 380,
        boxSizing: 'border-box',
        animation: 'mi-toast-in 0.18s ease-out',
      }}
    >
      {/* Icon chip */}
      <div style={{
        width: 28, height: 28, flexShrink: 0,
        borderRadius: 8,
        background: v.bgTint,
        color: v.accent,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <v.Icon size={15} />
      </div>

      {/* Copy */}
      <div style={{ flex: 1, minWidth: 0, paddingTop: 2 }}>
        {title && (
          <div style={{
            fontSize: 13, fontWeight: 700, color: 'var(--text)',
            marginBottom: 2, lineHeight: 1.3,
          }}>
            {title}
          </div>
        )}
        {message && (
          <div style={{
            fontSize: 12.5, color: 'var(--text-2)',
            lineHeight: 1.45,
            wordBreak: 'break-word',
          }}>
            {message}
          </div>
        )}
      </div>

      {/* Close */}
      <button
        type="button"
        onClick={() => onDismiss?.()}
        aria-label="Dismiss"
        style={{
          width: 24, height: 24, flexShrink: 0,
          background: 'transparent',
          border: 'none',
          borderRadius: 6,
          color: 'var(--text-muted)',
          cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          alignSelf: 'flex-start',
          marginTop: 2,
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-2)'; e.currentTarget.style.color = 'var(--text-2)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-muted)'; }}
      >
        <Icon.x size={14} />
      </button>
    </div>
  );
}

// Container helper exposed for the Provider — handles desktop vs mobile
// positioning + stacking layout. Toasts pile vertically with a small gap.
export function ToastStack({ children }) {
  const isMobile = useIsMobile();
  return (
    <div
      style={{
        position: 'fixed',
        top: isMobile ? 12 : 20,
        right: isMobile ? 12 : 20,
        left: isMobile ? 12 : 'auto',
        zIndex: 1200, // above modals
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        pointerEvents: 'none', // wrapper itself doesn't catch clicks
      }}
    >
      {/* Re-enable pointer-events on the children so close buttons work */}
      <style>{`@keyframes mi-toast-in {
        from { opacity: 0; transform: translateY(-6px); }
        to   { opacity: 1; transform: translateY(0); }
      }`}</style>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, pointerEvents: 'auto' }}>
        {children}
      </div>
    </div>
  );
}
